import { EApplicantType, ECreditApplicationStatus, EDecision, EDocumentStatus, EFinancingProgram } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { Typography } from '@mui/material'
import { getIdentityDocumentTypes, getIncomeVerificationDocumentsTypes } from '@src/data/income-selectors'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import StepStatusSection from '../../../App/components/StepStatusSection'
import { GetIconAndMessageForIncomeVerification } from '../../viewCreditApp-selectors'
import BankAccountProcedureDialog from './BankAccountProcedureDialog'
import IncomeVerificationMismatch from './IncomeVerificationMismatch'
import NameMismatch from './NameMismatch'
import StartIncomeVerification from './StartIncomeVerification'
import { getVerifiedIncome, useHasBankStatementOrVoidCheque, useHasRequiredDocuments } from './incomeVerification-hooks'

interface Props {
  applicant: ApplicantDto
  requiredDocuments: RequiredDocument[]
  creditDecision: EDecision
  applicationStatus: ECreditApplicationStatus
  creditApplicationId: string
  merchant: Merchant
  financingProgramId: EFinancingProgram
  canEdit: boolean
}

const IncomeVerificationTile = ({
  applicant,
  requiredDocuments,
  creditDecision,
  applicationStatus,
  creditApplicationId,
  merchant,
  financingProgramId,
  canEdit,
}: Props) => {
  const { t } = useTranslation()
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false)

  const applicantType = applicant.isPrimaryApplicant ? EApplicantType.Applicant : EApplicantType.CoApplicant
  const canStartStep = creditDecision === EDecision.Approved
  const isDone = applicant.incomeVerification?.allIncomeTasksConfirmed ?? false
  const showVerifiedIncome =
    isDone || (applicant.incomeVerification?.incomeMismatch && !applicant.incomeVerification.nameMismatch)

  const hasIncomeServiceAdjustment = applicant.incomeVerification?.hasIncomeServiceAdjustment ?? false
  const hasBankStatementOrVoidCheque = useHasBankStatementOrVoidCheque(requiredDocuments, applicantType)

  const incomeVerificationDocumentsTypes = getIncomeVerificationDocumentsTypes
  const identityDocumentTypes = getIdentityDocumentTypes
  const hasIncomeDocumentsPendingUpload = useHasRequiredDocuments(
    requiredDocuments,
    Object.values(incomeVerificationDocumentsTypes),
    applicantType,
    EDocumentStatus.AwaitingDocument,
  )
  const hasIncomeDocumentsPendingReview = useHasRequiredDocuments(
    requiredDocuments,
    Object.values(incomeVerificationDocumentsTypes),
    applicantType,
    EDocumentStatus.AwaitingApproval,
  )
  const hasIncomeDocumentsApproved = useHasRequiredDocuments(
    requiredDocuments,
    Object.values(incomeVerificationDocumentsTypes),
    applicantType,
    EDocumentStatus.Approved,
  )
  const hasIdentityDocumentPendingUpload = useHasRequiredDocuments(
    requiredDocuments,
    Object.values(identityDocumentTypes),
    applicantType,
    EDocumentStatus.AwaitingDocument,
  )
  const hasIdentityDocumentPendingReview = useHasRequiredDocuments(
    requiredDocuments,
    Object.values(identityDocumentTypes),
    applicantType,
    EDocumentStatus.AwaitingApproval,
  )

  const hasIdentityDocumentApproved = useHasRequiredDocuments(
    requiredDocuments,
    Object.values(identityDocumentTypes),
    applicantType,
    EDocumentStatus.Approved,
  )

  const { iconMessage, iconType } = GetIconAndMessageForIncomeVerification(
    applicant,
    canStartStep,
    hasIncomeDocumentsPendingReview,
    hasIncomeDocumentsPendingUpload,
    hasIncomeDocumentsApproved,
    isDone,
    applicationStatus,
    hasIdentityDocumentPendingReview,
    hasIdentityDocumentApproved,
  )

  return (
    <StepStatusSection
      title={t('viewCreditApplication.incomeVerificationSectionTile')}
      subtitle={`${applicant.firstName} ${applicant.lastName} (${t(`common.${applicantType}`)})`}
      iconMessage={iconMessage ? t(`creditApplicationStatus.${iconMessage}`) : null}
      iconType={iconType}
      onFooterClicked={
        isDone || applicationStatus === ECreditApplicationStatus.Cancelled
          ? undefined
          : () => setOpenDetailsDialog(true)
      }
    >
      {!isDone && applicationStatus === ECreditApplicationStatus.Cancelled ? (
        <Typography variant="body1">{t('viewCreditApplication.creditAppCancelled')}</Typography>
      ) : (
        <>
          {/* Informative Dialog */}
          {openDetailsDialog && (
            <BankAccountProcedureDialog
              financingProgramId={financingProgramId}
              open={openDetailsDialog}
              onClose={() => setOpenDetailsDialog(false)}
              applicantType={applicantType}
              creditApplicationId={creditApplicationId}
              canSkipFlinks={merchant.canSkipFlinks}
            />
          )}

          {/* Content */}
          {canStartStep ? (
            <>
              {/* Income Verification Skipped */}
              {!applicant.incomeVerification?.thirdPartyFlowCompleted && !isDone && (
                <StartIncomeVerification
                  applicant={applicant}
                  hasIncomeDocumentsPendingUpload={hasIncomeDocumentsPendingUpload}
                  hasIncomeDocumentsPendingReview={hasIncomeDocumentsPendingReview}
                  hasIncomeDocumentsApproved={hasIncomeDocumentsApproved}
                  creditApplicationId={creditApplicationId}
                  financingProgramId={financingProgramId}
                  canSkipFlinks={merchant.canSkipFlinks}
                  canEdit={canEdit}
                />
              )}
              {/* Flinks is processing */}
              {applicant.incomeVerification?.thirdPartyFlowCompleted && !hasBankStatementOrVoidCheque && (
                <p>{t('incomeVerification.message.waitingForFlinks')}</p>
              )}
              {/* Name Mismatch */}
              {applicant.incomeVerification?.nameMismatch && (
                <NameMismatch
                  financingProgramId={financingProgramId}
                  hasIdentityDocumentsPendingReview={hasIdentityDocumentPendingReview}
                  hasIdentityDocumentsPendingUpload={hasIdentityDocumentPendingUpload}
                  allIncomesConfirmed={isDone}
                  applicantType={applicantType}
                  creditApplicationId={creditApplicationId}
                  canEdit={canEdit}
                />
              )}
              {/* Income Mismatch */}
              {applicant.incomeVerification?.incomeMismatch &&
                !applicant.incomeVerification.nameMismatch &&
                !isDone && (
                  <IncomeVerificationMismatch
                    financingProgramId={financingProgramId}
                    applicant={applicant}
                    hasIncomeDocumentsPendingReview={hasIncomeDocumentsPendingReview}
                    hasIncomeDocumentsPendingUpload={hasIncomeDocumentsPendingUpload}
                    hasIncomeServiceAdjustment={hasIncomeServiceAdjustment}
                    creditApplicationId={creditApplicationId}
                    canEdit={canEdit}
                  />
                )}
              {/* Verified Income */}
              {canEdit && showVerifiedIncome && (
                <div>
                  <Typography variant="subtitle1">{t('incomeVerification.verifiedIncome')}</Typography>
                  <Typography variant="body1">
                    {formatCurrencyNoDecimals(
                      getVerifiedIncome(hasBankStatementOrVoidCheque, hasIncomeDocumentsApproved, applicant),
                    )}
                  </Typography>
                </div>
              )}
              {/* Submitted Income */}
              {canEdit && (
                <div>
                  <Typography variant="subtitle1">{t('incomeVerification.totalIncome')}</Typography>
                  <Typography variant="body1">
                    {applicant.incomeVerification &&
                      formatCurrencyNoDecimals(applicant.incomeVerification.declaredIncome)}
                  </Typography>
                </div>
              )}
              {applicant.incomeVerification?.allIncomeTasksConfirmed && (
                <div>
                  <Typography variant="body1">{t('incomeVerification.incomeVerified')}</Typography>
                </div>
              )}
            </>
          ) : (
            <Typography variant="body1">{t('incomeVerification.notStarted')}</Typography>
          )}
        </>
      )}
    </StepStatusSection>
  )
}

export default IncomeVerificationTile
